import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUser, uploadProfileImage } from '../../services/userService';
import { invalidateCachedProfileImage } from '../../utils/profileImageCache';
import ProfileAvatar from './ProfileAvatar';
import Loader from './Loader';
import Modal from '../ui/Modal';
import { formatMMDDYYYY } from '../../utils/dateUtils';

const ROLE_MAP = { 1: 'SPED Director', 2: 'SPED Teacher' };

const statusConfig = {
  active: { label: 'Active', cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
  inactive: { label: 'Inactive', cls: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' },
  suspended: { label: 'Suspended', cls: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' },
};

function formatMobile(val) {
  if (!val) return null;
  const s = String(val);
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return s;
}

function displayValue(val) {
  if (val === null || val === undefined || val === '') return '—';
  return val;
}

export default function ProfileModal({ onClose }) {
  const navigate = useNavigate();
  const { user: authUser, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [imageRefreshKey, setImageRefreshKey] = useState(0);

  useEffect(() => {
    if (!authUser?.id) return;
    setLoading(true);
    getUser(authUser.id)
      .then(({ data }) => {
        setProfile(data.data);
        if (setUser) setUser(data.data);
      })
      .catch(() => setProfile(authUser))
      .finally(() => setLoading(false));
  }, [authUser?.id]); // eslint-disable-line

  if (!authUser) return null;

  const user = profile || authUser;
  const fullName = [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(' ');
  const roleName = user.role_name || ROLE_MAP[user.role] || 'No Role';
  const status = statusConfig[user.is_active] || { label: user.is_active || 'Unknown', cls: 'text-gray-500 bg-gray-50 border-gray-200' };

  const addressParts = [user.address_line1, user.address_line2].filter(Boolean);
  const locationParts = [user.city, user.state, user.zipcode ? String(user.zipcode) : null].filter(Boolean);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      await uploadProfileImage(user.id, file);
      invalidateCachedProfileImage('user', user.id);
      const updatedUser = { ...user, has_profile_image: true };
      setProfile(updatedUser);
      if (setUser) setUser(updatedUser);
      setImageRefreshKey((k) => k + 1);
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          localStorage.setItem('user', JSON.stringify({ ...parsed, has_profile_image: true }));
        } catch { /* ignore */ }
      }
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = () => {
    onClose();
    navigate(`/users/${user.id}/edit`);
  };

  return (
    <Modal open onClose={onClose} size="lg" title="Profile">
      <div className="-mx-5 -mt-4 mb-4 px-6 pt-5 pb-5 bg-gradient-to-br from-indigo-50 dark:from-indigo-600/20 to-white dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={handleEdit}
            disabled={loading}
            title="Edit profile"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>

        <div className="flex items-start gap-4">
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg shadow-indigo-500/20 ring-4 ring-white dark:ring-slate-800">
              <ProfileAvatar
                type="user"
                id={user.id}
                variant="solid"
                hasProfileImage={user.has_profile_image}
                refreshKey={imageRefreshKey}
              />
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Change profile picture"
              className="absolute -bottom-1 -right-1 w-6 h-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full flex items-center justify-center shadow-md transition-colors disabled:opacity-60"
            >
              {uploading ? (
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>

          <div className="min-w-0 flex-1 mt-1">
            <p className="text-gray-900 dark:text-white font-bold text-lg leading-tight">{fullName}</p>
            <p className="text-gray-500 dark:text-slate-400 text-sm truncate mt-1">{user.email}</p>
            <p className="text-gray-400 dark:text-slate-500 text-xs mt-1">User ID {user.id}</p>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <span className="inline-block px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-600/30 text-indigo-600 dark:text-indigo-300 text-xs font-medium border border-indigo-200 dark:border-indigo-500/30">
                {roleName}
              </span>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${status.cls}`}>
                {status.label}
              </span>
            </div>
          </div>
        </div>

        {uploadError && (
          <p className="mt-2 text-xs text-red-500 dark:text-red-400">{uploadError}</p>
        )}
      </div>

      <div className="px-1 py-2">
        {loading ? (
          <Loader text="Loading profile..." />
        ) : (
          <div className="space-y-3.5">
            <InfoRow icon={<PhoneIcon />} label="Mobile" value={displayValue(formatMobile(user.mobile))} />
            {user.role === 2 && (
              <InfoRow icon={<SchoolIcon />} label="School" value={displayValue(user.school_name)} />
            )}
            <InfoRow icon={<LocationIcon />} label="Location" value={displayValue(locationParts.join(', '))} />
            <InfoRow icon={<HomeIcon />} label="Address" value={displayValue(addressParts.join(', '))} />
            <InfoRow icon={<CalendarIcon />} label="Hired" value={displayValue(formatMMDDYYYY(user.hire_date) || null)} />
            {user.termination_date && (
              <InfoRow icon={<CalendarIcon />} label="End Date" value={formatMMDDYYYY(user.termination_date)} />
            )}
          </div>
        )}
      </div>

      {user.user_notes && user.user_notes.length > 0 && (
        <div className="px-1 pb-2 border-t border-gray-100 dark:border-slate-700 pt-4">
          <p className="text-xs text-gray-400 dark:text-slate-500 font-semibold uppercase tracking-wider mb-2">Notes</p>
          <div className="space-y-2">
            {user.user_notes.map((n, i) => (
              <p key={i} className="text-sm text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-700/40 rounded-lg px-3 py-2 leading-relaxed">
                {n.note || (typeof n === 'string' ? n : JSON.stringify(n))}
              </p>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function InfoRow({ icon, label, value, valueClass = '' }) {
  return (
    <div className="flex items-start gap-3 py-0.5">
      <span className="text-gray-400 dark:text-slate-500 flex-shrink-0 mt-0.5">{icon}</span>
      <span className="text-gray-500 dark:text-slate-400 text-sm w-28 flex-shrink-0 font-medium">{label}</span>
      <span className={`text-gray-800 dark:text-slate-200 text-sm break-words min-w-0 flex-1 leading-relaxed ${valueClass}`}>{value}</span>
    </div>
  );
}

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);
const SchoolIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);
const LocationIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const HomeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);
const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
